import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Edit2, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OrderNumberManager - Collapsible panel to manage order number sequence
 * Extracted from AdminDashboard.js for maintainability
 */
export default function OrderNumberManager({ getAuthHeader, onRefresh }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [nextOrderNumber, setNextOrderNumber] = useState('');
  const [settingCounter, setSettingCounter] = useState(false);
  const [currentNextOrderNumber, setCurrentNextOrderNumber] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch current counter on mount and when expanded
  useEffect(() => {
    fetchCounter();
  }, []);
  
  useEffect(() => {
    if (isExpanded) {
      fetchCounter();
    }
  }, [isExpanded]);

  const fetchCounter = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/orders/counter`, {
        headers: getAuthHeader()
      });
      setCurrentNextOrderNumber(res.data.next_order_number);
    } catch (error) {
      console.error('Failed to fetch order counter:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSetNextOrderNumber = async () => {
    const num = parseInt(nextOrderNumber, 10);
    if (isNaN(num) || num < 1) {
      toast.error('PLEASE ENTER A VALID NUMBER');
      return;
    }
    
    if (!window.confirm(`SET NEXT ORDER NUMBER TO BLK-${num.toString().padStart(6, '0')}?`)) return;
    
    try {
      setSettingCounter(true);
      await axios.put(
        `${API}/admin/orders/set-counter`,
        { next_order_number: num },
        { headers: getAuthHeader() }
      );
      toast.success(`NEXT ORDER WILL BE BLK-${num.toString().padStart(6, '0')}`);
      setNextOrderNumber('');
      setCurrentNextOrderNumber(num);  // Update local state
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'FAILED TO SET COUNTER');
    } finally {
      setSettingCounter(false);
    }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-900/50">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-800/50 transition-colors"
      >
        <span className="font-mono text-sm text-zinc-400 flex items-center gap-2">
          <Edit2 className="w-4 h-4" />
          ORDER #
        </span>
        <div className="flex items-center gap-2">
          {currentNextOrderNumber && (
            <span className="font-mono text-sm text-tiffany-500 whitespace-nowrap">
              BLK-{currentNextOrderNumber.toString().padStart(6, '0')}
            </span>
          )}
          {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Current Next Order Display */}
          <div className="p-3 border border-tiffany-500/30 bg-tiffany-500/5">
            <div className="flex items-center justify-between mb-1">
              <p className="font-mono text-xs text-zinc-500">NEXT ORDER NUMBER WILL BE:</p>
              <button 
                onClick={fetchCounter}
                disabled={loading}
                className="text-zinc-500 hover:text-tiffany-500 transition-colors"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {loading ? (
              <p className="font-mono text-xl text-zinc-500">LOADING...</p>
            ) : currentNextOrderNumber ? (
              <p className="font-mono text-xl text-tiffany-500 font-bold">
                BLK-{currentNextOrderNumber.toString().padStart(6, '0')}
              </p>
            ) : (
              <p className="font-mono text-xl text-zinc-500">NOT SET</p>
            )}
          </div>
          
          {/* Set Next Order Number */}
          <div className="p-3 border border-zinc-700 bg-zinc-900">
            <label className="block font-mono text-xs text-zinc-500 mb-2">
              CHANGE NEXT ORDER NUMBER
            </label>
            <p className="font-mono text-[10px] text-zinc-600 mb-2 uppercase">
              ENTER THE NUMBER (WITHOUT BLK- PREFIX)
            </p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 font-mono text-zinc-500 text-sm">BLK-</span>
                <Input
                  type="number"
                  value={nextOrderNumber}
                  onChange={(e) => setNextOrderNumber(e.target.value)}
                  placeholder="059353"
                  className="h-9 pl-12 bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none"
                  data-testid="next-order-number-input"
                />
              </div>
              <Button
                onClick={handleSetNextOrderNumber}
                disabled={settingCounter || !nextOrderNumber}
                className="h-9 px-4 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono text-xs rounded-none disabled:opacity-50"
                data-testid="set-next-order-btn"
              >
                {settingCounter ? 'SETTING...' : 'SET'}
              </Button>
            </div>
          </div>
          
          {/* Instructions */}
          <div className="p-3 border border-amber-700/30 bg-amber-500/5">
            <p className="font-mono text-xs text-amber-500 mb-1">TO EDIT EXISTING ORDER IDS:</p>
            <p className="font-mono text-[10px] text-zinc-400 uppercase">
              CLICK ON AN ORDER BELOW → EXPAND IT → CLICK THE ORDER ID (BLK-XXXXXX) → EDIT AND SAVE
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
